import { css, jsx } from '@emotion/react';
import { TooltipWrapper } from '../../display/TooltipWrapper/TooltipWrapper.es.js';
import { Spinner } from '../../display/Spinner/Spinner.es.js';
import { kebabCaseAriaAttrs } from '../../../utils/formatAriaAttrKeys.es.js';
import { Animation } from '../../../theme/Animation.es.js';
import { Colors } from '../../../theme/Colors.es.js';
import '../../../theme/Shadows.es.js';
import { Typography } from '../../../theme/Typography.es.js';
import '../../../theme/ZinniaStylesReset.es.js';
import { labelStyles, withModifier } from '@zapier/style-encapsulation';

const Styles = labelStyles("ToggleSwitch", {
  root: (props) => [
    css`
      align-items: stretch;
      color: ${Colors.neutral800};
      cursor: pointer;
      display: inline-flex;
      overflow: hidden;
      /*
      PDL ToggleSwitches shouldn't have a border, so we're setting the border to the same color as the background to make it appear that it doesn't have a border.
      */
      border: 1px solid var(--zds-background-strongest, ${Colors.neutral600});
      transition: all ${Animation.transitionDuration} ease-in-out;

      /*
      The PDL focus ring is rendered as pseudo-element with a border to support a smaller radius than the border-radius of the label.
      It is initially rendered without a border so that its transition can be animated.
      */
      &::before {
        content: '';
        position: absolute;
        top: 2px;
        left: -4px;
        right: -4px;
        bottom: -4px;
        border-radius: var(--zds-radius-small);
        transition: all ${Animation.transitionDuration} ease-in-out;
        /* Initially-transparent border prevents the border from momentarily appearing  */
        border-color: transparent;
      }

      &:has(:focus-visible):not(:hover)::before {
        border: 2px solid var(--zds-ui-primary-stronger, transparent);
        border-radius: var(--zds-radius-small);
      }

      /* Legacy styles use a box-shadow as the focus ring. The ring needs to be pressed against the edge of the label. */
      &:has(:focus-visible):not(:hover) {
        box-shadow: 0 0 0 var(--zds-radius-none, 2px) ${Colors.blue};
        border-color: transparent;
      }
    `,
    props.size === "small" && withModifier(props.size, css`
          width: 40px;
          height: 22px;
          border-radius: 20px;
          font-size: 0px;
          line-height: 0px;
          padding: 3px;
        `),
    props.size === "medium" && withModifier(props.size, css`
          min-width: var(--zds-size-36, 60px);
          width: var(--zds-size-36, 100%);
          height: var(--zds-size-20, 44px);
          border-radius: 30px;
          /* Ensures that dot is correctly aligned to the left/right edge for PDL and legacy styles */
          padding: var(--zds-border-width-default, 6px) var(--zds-size-2, 6px);
        `),
    !props.checked && withModifier("unchecked", css`
          background-color: var(
            --zds-background-strongest,
            ${Colors.neutral200}
          );
        `),
    props.checked && withModifier("checked", css`
          background-color: var(
            --zds-ui-primary-stronger,
            ${Colors.success100}
          );
          border: 1px solid var(--zds-ui-primary-stronger, ${Colors.success400});
        `),
    props.indeterminate && withModifier("indeterminate", css`
          background-color: var(
            --zds-background-strongest,
            ${Colors.warning200}
          );
        `),
    (props.disabled || props.isLoading) && withModifier("disabled", css`
          background-color: ${Colors.neutral200};
          color: ${Colors.neutral500};
          cursor: not-allowed;
          border: ${`1px solid var(--zds-background-strongest, ${Colors.neutral600}`});
        `)
  ],
  innerWrap: css`
    position: relative;
    display: flex;
    justify-content: center;
    width: 100%;
  `,
  // Still needs to be "visible" so it can be tabbed to,
  // but it shouldn't render anything. Take it out of layout
  // flow and hide it with `opacity` for good measure. Also
  // stretch it across the container so tools like VoiceOver
  // show an outline in the correct location.
  input: css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    z-index: -1;
  `,
  spinner: [
    css`
      position: absolute;
      top: 0;
      bottom: 0;
      right: 0;
      left: 0;
      display: flex;
      justify-content: center;
      align-items: center;

      div[data-testid='spinner'] {
        height: var(--zds-size-16, 20px);
        width: var(--zds-size-16, 20px);
      }
    `
  ],
  // This node's text should be centered within the available space
  // outside of the dot. For instance when the dot is on the left,
  // the text will be to the right of the dot, but centered in that
  // available space. When the dot is on the right, the text will be
  // to the left of the dot, but centered in that available space.
  text: (props) => [
    css`
      display: grid;
      // Define to allow 'textInner' nodes to easily stack
      // on top of each other while preserving their widths,
      // setting up the layout for a nice transition.
      grid-template-areas: 'text';
      align-items: center;
      width: 100%;
      user-select: none;
      text-align: center;
      transition: padding ${Animation.transitionDuration} ease-in-out;
    `,
    !props.checked && withModifier(`unchecked-${props.size}`, {
      paddingLeft: "calc(30px + 8px)",
      paddingRight: 0
    }),
    props.checked && withModifier(`checked-${props.size}`, {
      paddingLeft: 0,
      paddingRight: "calc(30px + 8px)"
    }),
    props.indeterminate && withModifier("indeterminate", css`
          padding-left: 0;
          padding-right: 0;
        `),
    props.isLoading && withModifier("loading", css`
          visibility: hidden;
          height: 0;
        `)
  ],
  // Render all text, stacked on top of each other, and then visibly hide
  // the `isHidden` text. This ensures that `ToggleSwitch` is as wide as
  // its longest text label, even if that label is hidden.
  textInner: ({ isHidden }) => [
    css`
      // See note at 'grid-template-areas' definition.
      grid-area: text;
      ${Typography.paragraph3Bold};
      font-size: var(--zds-space-0, 16px);
      transition: opacity 0.1s ease-in-out;
    `,
    isHidden && withModifier("hidden", css`
          opacity: 0;
        `)
  ],
  dot: (props) => [
    css`
      display: block;
      border-radius: 100%;
      background-color: var(--zds-gray-warm-6, ${Colors.neutral600});
      position: absolute;
      top: 0;
      transition: all ${Animation.transitionDuration} ease-in-out;
      width: var(--zds-size-16, 30px);
      height: var(--zds-size-16, 30px);
    `,
    !props.checked && withModifier("unchecked", css`
          left: 0;
          transform: translateX(0);
        `),
    props.checked && // Using `left` and `transform` for proper `transition`.
    // Using `right: auto` for instance won't transition properly.
    // `translate` is used over `calc` since `translate`'s argument
    // is the size of the `dot` itself rather than its parent.
    withModifier("checked", css`
          background-color: var(--zds-prime-white, ${Colors.success400});
          left: 100%;
          transform: translateX(-100%);
        `),
    props.disabled && withModifier("disabled", css`
          background-color: ${Colors.neutral400};
        `),
    props.isLoading && withModifier("loading", css`
          visibility: hidden;
          height: 0;
        `)
  ]
});
const InnerText = ({ children, isHidden }) => {
  return jsx("span", { "aria-hidden": "true", css: Styles.textInner({ isHidden }), "data-zds": true }, children);
};
const getAriaLabel = (props) => {
  if (props.ariaLabel) {
    return props.ariaLabel;
  }
  if (props.isLoading) {
    return "Loading";
  }
  if (props.indeterminate) {
    return typeof props.indeterminateText === "string" ? props.indeterminateText : "Indeterminate";
  }
  return props.checked ? props.checkedText : props.uncheckedText;
};
const getTooltipContent = (props) => props.disabled && props.showDisabledTooltip ? "Disabled" : "";
const ToggleSwitch = ({ ariaLabel = void 0, checked = void 0, checkedText = "On", disabled = void 0, id = void 0, indeterminate = void 0, indeterminateText = "N/A", isLoading = false, name = void 0, onChange = () => {
}, showDisabledTooltip = true, size = "medium", uncheckedText = "Off", value = void 0 }) => {
  const props = {
    ariaLabel,
    checked,
    checkedText,
    disabled,
    id,
    indeterminate,
    indeterminateText,
    isLoading,
    name,
    onChange,
    showDisabledTooltip,
    size,
    uncheckedText,
    value
  };
  return jsx(TooltipWrapper, { content: getTooltipContent(props) }, ({ childProps }) => jsx(
    "label",
    Object.assign({}, kebabCaseAriaAttrs(childProps), { css: Styles.root(props), "data-zds": true }),
    jsx(
      "span",
      { css: Styles.innerWrap, "data-zds": true },
      jsx("input", { "aria-disabled": props.disabled || props.isLoading, "aria-label": getAriaLabel(props), checked: props.checked, css: Styles.input, disabled: props.disabled || props.isLoading, "data-zds": true, id: props.id, name: props.name, onChange: props.onChange, type: "checkbox", value: props.value }),
      props.isLoading && jsx(
        "span",
        { css: Styles.spinner, "data-zds": true },
        jsx(Spinner, null)
      ),
      jsx(
        "span",
        { css: Styles.text(props) },
        jsx(InnerText, { isHidden: props.indeterminate || !!props.checked }, props.uncheckedText),
        jsx(InnerText, { isHidden: props.indeterminate || !props.checked }, props.checkedText),
        jsx(InnerText, { isHidden: !props.indeterminate }, props.indeterminateText)
      ),
      !props.indeterminate && !props.isLoading && jsx("span", { "aria-hidden": "true", css: Styles.dot(props), role: "presentation", "data-zds": true })
    )
  ));
};

export { ToggleSwitch };
//# sourceMappingURL=ToggleSwitch.es.js.map
